import cn from 'classnames';
import { useState } from 'react';
import { trackError } from '#api/errors.ts';
import { CheckIcon, CopyIcon } from '#assets/ReactSvg.ts';
import { Tooltip } from '#components/atoms/tooltip/Tooltip.tsx';
import { useTimeout } from '#hooks/useTimeout.ts';
import { copyToClipboard } from '#util/copy-to-clipboard.ts';
import styles from './CopyPill.module.css';

type Props = {
  /** The text copied to the clipboard. */
  value: string;
  /** Visible text, when it differs from `value` (e.g. a truncated address). Defaults to `value`. */
  displayValue?: string;
  className?: string;
};

export function CopyPill({ value, displayValue = value, className }: Props) {
  const initialTooltip = 'Copy';
  const successTooltip = 'Copied!';
  const [tooltip, setTooltip] = useState(initialTooltip);
  const [isHovered, setIsHovered] = useState(false);

  const { start: resetTooltipAfterDelay, clear: clearTimeout } = useTimeout(() => {
    setTooltip(initialTooltip);
  }, 2000);

  const handleClick = () => {
    copyToClipboard(value)
      .then(() => {
        setTooltip(successTooltip);
        resetTooltipAfterDelay();
        return;
      })
      .catch((err: unknown) => {
        console.error('Could not copy');
        setTooltip('Unable to copy');
        trackError(err);
      });
  };

  const copied = tooltip === successTooltip;
  // Provides the button's accessible name (used by screen readers and Ctrl+F)
  // and, because it differs from the visible (possibly truncated) text,
  // keeps `findByText(value)` unambiguous in tests.
  const ariaName = copied ? successTooltip : `Copy ${value}?`;

  return (
    <Tooltip
      content={tooltip}
      onHidden={() => {
        setTooltip(initialTooltip);
        clearTimeout();
      }}
      render={
        <button
          type="button"
          className={cn(styles.CopyPill, className, {
            [styles.CopyPill___copied]: copied,
          })}
          onClick={handleClick}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          <span className={styles.CopyPill_text} aria-hidden={true}>
            {displayValue}
          </span>
          {/** Full value for screen readers and CTRL+F */}
          <span className="visually-hidden">{ariaName}</span>
          <span className={styles.CopyPill_icon} aria-hidden={true}>
            {copied ?
              <CheckIcon width={12} height={12} />
            : isHovered ?
              <CopyIcon width={12} height={12} />
            : null}
          </span>
        </button>
      }
    />
  );
}
